import type { AdminRecord } from '@/types/record'
import type {
  ActionResult,
  AdminNotice,
  Paginated,
  RequestScenario,
  TaskRecord,
} from '@/types/admin'
import type {
  FilterState,
  FormFieldDef,
  ListQuery,
  PageDefinition,
  SelectOption,
} from '@/types/page'
import type { MasterConfig } from '@/data/masters'
import { type SeedDatabase, ASSET_FOLDERS, DISEASE_TAGS, HEALTH_TAGS, LINK_CATEGORIES } from '@/data/seed'
import { database, resetPrototypeDatabase } from '@/data/adapters/memory'
import { ROLE_PERMISSIONS } from '@/data/roles'
import { USER_PAGE_OPTIONS } from '@/data/page-defs/commerce'
import { PERMISSION_GROUPS } from '@/data/registry'
import { queryRecords, summarize } from './query'
import { toPlain } from '@/utils/clone'
import { formatDate, formatDateTime, isoHoursAgo, nowIso } from '@/utils/format'
import { businessNo, nextSequence, requestNo } from '@/utils/id'
import { ApiError, BusinessConflict } from '@/utils/errors'

export interface PlatformSettings {
  platformName: string
  servicePhone: string
  serviceHours: string
  brandColor: string
  passwordMinLength: number
  sessionMinutes: number
  mfaRequired: boolean
  loginFailureLock: number
  defaultChannels: string[]
  quietStart: string
  quietEnd: string
  retryCount: number
  alertSlaMinutes: number
  ticketSlaMinutes: number
  maintenanceEnabled: boolean
  maintenanceMessage: string
  maintenanceUntil: string
  updatedAt: string
  updatedBy: string
}

export interface ActionOutcome {
  message: string
  row?: AdminRecord
  info?: { title: string; lines: Array<{ label: string; value: string }>; note?: string }
  download?: { filename: string; content: string }
  tempPassword?: string
  navigate?: string
}

export interface DashboardStats {
  usersTotal: number
  activeUsers: number
  families: number
  adherence: number
  pendingAlerts: number
  highAlerts: number
  onlineRate: number
  deviceTotal: number
  revenueFen: number
  paidOrders: number
  pendingDrugs: number
  urgentTickets: number
  failedTasks: number
  trend: Array<{ day: string; users: number; records: number; alerts: number; orders: number }>
  risk: Array<{ label: string; value: number; tone: string }>
  channels: Array<{ label: string; value: number }>
  regions: Array<{ label: string; users: number; families: number; alerts: number }>
  todos: Array<{ id: string; title: string; meta: string; route: string; tone: string }>
  recent: AdminRecord[]
}

export interface SearchResultGroup {
  label: string
  items: Array<{ id: string; title: string; sub: string; route: string; group: string }>
}

const TERMINAL_TASK = ['success', 'partial_success', 'failed', 'cancelled']

function defaultSettings(): PlatformSettings {
  return {
    platformName: '康护健康',
    servicePhone: '400-820-6606',
    serviceHours: '每日 08:00–21:00',
    brandColor: '#08B578',
    passwordMinLength: 8,
    sessionMinutes: 480,
    mfaRequired: true,
    loginFailureLock: 5,
    defaultChannels: ['站内信', '短信'],
    quietStart: '21:30',
    quietEnd: '07:30',
    retryCount: 3,
    alertSlaMinutes: 30,
    ticketSlaMinutes: 120,
    maintenanceEnabled: false,
    maintenanceMessage: '系统正在升级健康档案与消息服务，期间管理端只读。',
    maintenanceUntil: '2026-10-18T02:00:00+08:00',
    updatedAt: isoHoursAgo(30),
    updatedBy: '陈知远',
  }
}

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values))
}

function dateKey(value: unknown): string {
  return formatDate(value)
}

export class PrototypeAdapter {
  private data: Record<string, unknown> = {}
  settings: PlatformSettings = defaultSettings()

  constructor() {
    // The prototype adapter shares the same reactive in-memory database as the
    // Pinia store. This keeps tables, options, master data and dashboards in
    // sync without a second hidden copy of the seed.
    this.data = database as unknown as Record<string, unknown>
    this.settings = defaultSettings()
    this.augmentSeed(database)
    this.syncDerived()
  }

  reset(): void {
    const seed = resetPrototypeDatabase()
    this.data = database as unknown as Record<string, unknown>
    this.settings = defaultSettings()
    this.augmentSeed(seed)
    this.syncDerived()
  }

  private augmentSeed(seed: SeedDatabase): void {
    const now = nowIso()
    const drugs = seed.drugs
    const devices = seed.devices
    const plans = seed.membership_plans
    const faq = seed.faq
    const reports = seed.reports

    // Seed messages must reference real template records. Without this the
    // template dropdown, message detail and "referenced template" conflict
    // rule would all point at different names.
    for (const message of seed.messages) {
      const match = seed.templates.find(template =>
        template.template_type === message.message_type && template.status === 'active',
      )
      if (match) message.template_name = match.name
    }

    this.data.health_tags = HEALTH_TAGS.map((name, index) => ({
      id: `tag-${index + 1}`,
      name,
      description: `用于家庭档案、消息受众与随访筛选的${name}标签`,
      status: index === HEALTH_TAGS.length - 1 ? 'disabled' : 'active',
      sort: index + 1,
      created_at: now,
      updated_at: now,
      row_version: 1,
    }))
    this.data.drug_categories = unique(drugs.map(item => String(item.category_name))).map((name, index) => ({
      id: `drug-category-${index + 1}`,
      name,
      description: `${name}药品统一分类，供筛选、用药计划与药事审核复用`,
      status: 'active',
      created_at: now,
      updated_at: now,
      row_version: 1,
    }))
    this.data.device_models = unique(devices.map(item => String(item.model_name))).map((name, index) => {
      const sample = devices.find(item => item.model_name === name)
      return {
        id: `device-model-${index + 1}`,
        name,
        model_code: `MD-${String(index + 1).padStart(3, '0')}`,
        device_type: sample?.device_type ?? '血压计',
        capabilities: sample?.device_type === '血压计' ? ['血压', '心率'] : sample?.device_type === '血糖仪' ? ['血糖'] : ['心率'],
        status: index === 6 ? 'disabled' : 'active',
        created_at: now,
        updated_at: now,
        row_version: 1,
      }
    })
    this.data.benefits = seed.benefits.map((item, index) => ({
      ...item,
      sort: index + 1,
      status: item.status ?? 'active',
      description: item.description ?? '会员专享健康服务权益',
    }))
    this.data.link_categories = LINK_CATEGORIES.map((name, index) => ({
      id: `link-category-${index + 1}`,
      name,
      sort: index + 1,
      status: 'active',
      created_at: now,
      updated_at: now,
      row_version: 1,
    }))
    this.data.faq_categories = unique(faq.map(item => String(item.category_name))).map((name, index) => ({
      id: `faq-category-${index + 1}`,
      name,
      description: `${name}相关帮助内容`,
      sort: index + 1,
      status: 'active',
      created_at: now,
      updated_at: now,
      row_version: 1,
    }))
    this.data.report_templates = unique(reports.map(item => String(item.template_name))).map((name, index) => ({
      id: `report-template-${index + 1}`,
      name,
      report_type: index % 3 === 0 ? '周报' : index % 3 === 1 ? '月报' : '季度报告',
      description: '统一指标口径与版式，发送后用户端可见',
      status: index === 3 ? 'disabled' : 'active',
      created_at: now,
      updated_at: now,
      row_version: 1,
    }))

    let configs = seed.decoration_configs
    const pages = USER_PAGE_OPTIONS.map((option, index) => {
      const fullPath = `/${option.value}`
      const existing = (seed.decoration_pages as AdminRecord[]).find(item => item.full_path === fullPath)
      if (existing) return existing
      const title = option.label
      const pageKey = option.value.replace(/^pages\//, '').replace(/\/index/g, '').replace(/\//g, '-')
      const first = (configs[0]?.components as Array<Record<string, unknown>> | undefined) ?? []
      configs = [
        ...configs,
        {
          id: `dconfig-${pageKey}`,
          page_key: pageKey,
          page_title: title,
          full_path: fullPath,
          version: 1,
          status: 'draft',
          saved_by: '陈知远',
          saved_at: now,
          components: first.slice(0, 3).map((component, componentIndex) => ({
            ...component,
            id: `${pageKey}-${componentIndex + 1}`,
            sort: componentIndex + 1,
            visible: true,
          })),
          created_at: now,
          updated_at: now,
          row_version: 1,
        },
      ]
      return {
        id: `dpage-${pageKey}`,
        page_key: pageKey,
        title,
        full_path: fullPath,
        edit_scope: ['页面内容', '快捷入口', '运营位'],
        sort: index + 1,
        status: 'active',
        created_at: now,
        updated_at: now,
        row_version: 1,
      }
    })
    this.data.decoration_pages = pages
    this.data.decoration_configs = configs

    this.data.admin_notices = this.buildNotices()
    this.data.sessions = [
      { id: 'session-current', device: 'Chrome · macOS', ip: '10.24.18.32', at: isoHoursAgo(1), current: true },
      { id: 'session-2', device: 'Edge · Windows', ip: '10.24.18.28', at: isoHoursAgo(9), current: false },
      { id: 'session-3', device: 'Safari · iPad', ip: '10.24.18.11', at: isoHoursAgo(30), current: false },
    ]
    void plans
    void DISEASE_TAGS
  }

  private buildNotices(): AdminRecord[] {
    const notices: AdminRecord[] = []
    const alerts = this.table('alerts').filter(item => item.status === 'pending').slice(0, 4)
    for (const alert of alerts) {
      notices.push({
        id: `notice-${alert.id}`,
        type: 'alert',
        title: `${alert.severity}风险预警待处理`,
        body: `${alert.user_name} · ${alert.alert_type} ${alert.metric_value}`,
        route: '#/health/alerts',
        read: false,
        created_at: alert.triggered_at,
      })
    }
    const failed = this.table('async_tasks').filter(item => item.status === 'failed').slice(0, 2)
    for (const task of failed) {
      notices.push({
        id: `notice-${task.id}`,
        type: 'task',
        title: `${task.task_type}失败`,
        body: String(task.message),
        route: '#/settings/operations',
        read: false,
        created_at: task.created_at,
      })
    }
    const tickets = this.table('tickets').filter(item => item.priority === '紧急' && item.status !== 'closed').slice(0, 2)
    for (const ticket of tickets) {
      notices.push({
        id: `notice-${ticket.id}`,
        type: 'ticket',
        title: '紧急工单等待首响',
        body: `${ticket.ticket_no} · ${ticket.title}`,
        route: '#/support/tickets',
        read: false,
        created_at: ticket.created_at,
      })
    }
    notices.push({
      id: 'notice-security',
      type: 'security',
      title: '管理员登录安全提醒',
      body: '检测到一次异地登录尝试，已要求二次验证。',
      route: '#/settings/audit-logs',
      read: false,
      created_at: isoHoursAgo(7),
    })
    notices.push({
      id: 'notice-system',
      type: 'system',
      title: '原型数据已载入内存',
      body: '刷新页面会重置为可复现的初始业务数据，不会写入数据库。',
      route: '#/settings/general',
      read: true,
      created_at: isoHoursAgo(12),
    })
    return notices.sort((left, right) => String(right.created_at).localeCompare(String(left.created_at)))
  }

  private syncDerived(): void {
    const users = this.table('users')
    const families = this.table('families')
    const devices = this.table('devices')
    const regions = this.table('regions')
    const admins = this.table('admins')
    for (const region of regions) {
      const userCount = users.filter(item => item.region_id === region.id).length
      const familyCount = families.filter(item => item.region_id === region.id).length
      const deviceCount = devices.filter(item => item.region_id === region.id).length
      // Administrators with a scoped data range are resources too. A region
      // with an operator scope must be migrated before it can be disabled.
      const adminCount = region.level === 'district'
        ? admins.filter(item => item.data_scope !== 'all' && String(item.region_scope ?? '').includes(String(region.name))).length
        : 0
      region.admin_scope_count = adminCount
      region.resource_count = userCount + familyCount + deviceCount + adminCount
      region.resource_summary = [
        userCount ? `用户 ${userCount}` : '',
        familyCount ? `家庭 ${familyCount}` : '',
        deviceCount ? `设备 ${deviceCount}` : '',
        adminCount ? `管理员 ${adminCount}` : '',
      ].filter(Boolean).join('、') || '暂无关联资源'
    }
    for (const role of this.table('roles')) {
      role.member_count = this.table('admins').filter(admin =>
        Array.isArray(admin.role_codes)
        && (admin.role_codes as string[]).includes(String(role.code))
        && admin.status === 'active',
      ).length
    }
    const tags = this.table('health_tags')
    for (const tag of tags) {
      tag.usage_count = users.filter(item => Array.isArray(item.tags) && (item.tags as string[]).includes(String(tag.name))).length
    }
    const categories = this.table('drug_categories')
    for (const category of categories) {
      category.usage_count = this.table('drugs').filter(item => item.category_name === category.name).length
    }
    for (const model of this.table('device_models')) {
      model.usage_count = devices.filter(item => item.model_name === model.name).length
    }
    for (const benefit of this.table('benefits')) {
      benefit.usage_count = this.table('membership_plans').filter(item => Array.isArray(item.benefit_names) && (item.benefit_names as string[]).includes(String(benefit.name))).length
    }
    for (const category of this.table('link_categories')) {
      category.usage_count = this.table('links').filter(item => item.category_name === category.name).length
    }
    for (const category of this.table('faq_categories')) {
      category.usage_count = this.table('faq').filter(item => item.category_name === category.name).length
    }
    for (const template of this.table('report_templates')) {
      template.usage_count = this.table('reports').filter(item => item.template_name === template.name).length
    }
    for (const link of this.table('links')) {
      let count = 0
      for (const module of ['templates', 'tips', 'faq']) {
        count += this.table(module).filter(item => item.link_name === link.name).length
      }
      link.usage_count = count
    }
    for (const asset of this.table('assets')) {
      let count = 0
      for (const [module, fields] of Object.entries({
        drugs: ['image_name'],
        tips: ['cover_name'],
        membership_plans: ['cover_name'],
        reports: ['asset_name'],
      })) {
        for (const field of fields) {
          count += this.table(module).filter(item => item[field] === asset.name).length
        }
      }
      asset.usage_count = count
    }
  }

  /** Recompute derived references after direct in-memory transitions. */
  refreshDerived(): void {
    this.syncDerived()
  }

  table(module: string): AdminRecord[] {
    const value = this.data[module]
    if (!Array.isArray(value)) {
      this.data[module] = []
      return this.data[module] as AdminRecord[]
    }
    return value as AdminRecord[]
  }

  moduleFor(page: PageDefinition): string {
    if (page.seedKey && Array.isArray(this.data[page.seedKey])) return page.seedKey
    const map: Record<string, string> = {
      dashboard: 'users',
      analytics: 'orders',
      operations: 'async_tasks',
      decoration: 'decoration_pages',
      'general-settings': 'users',
      profile: 'admins',
    }
    return map[page.id] ?? page.id
  }

  list(page: PageDefinition, query: ListQuery): Paginated<AdminRecord> {
    const rows = this.table(this.moduleFor(page))
    const result = queryRecords(rows, page, toPlain(query))
    return { items: result.rows, total: result.total, page: result.page, pageSize: result.pageSize, summary: result.summary }
  }

  all(page: PageDefinition): AdminRecord[] {
    return this.table(this.moduleFor(page)).map(row => ({ ...row }))
  }

  find(module: string, id: string): AdminRecord | undefined {
    return this.table(module).find(row => String(row.id) === id)
  }

  findIn(page: PageDefinition, id: string): AdminRecord | undefined {
    const row = this.find(this.moduleFor(page), id)
    return row ? { ...row } : undefined
  }

  private nextId(module: string): string {
    return `${module}-${Date.now()}-${nextSequence()}`
  }

  private recordAudit(
    actor: string,
    module: string,
    action: string,
    resourceLabel: string,
    result: '成功' | '失败',
    before: AdminRecord | null,
    after: AdminRecord | null,
    detail: string,
  ): void {
    const logs = this.table('audit_logs')
    logs.unshift({
      id: `audit-${Date.now()}-${nextSequence()}`,
      actor_name: actor || '系统',
      actor_account: String((this.table('admins').find(item => item.name === actor)?.account) ?? 'system'),
      module,
      action,
      resource_label: resourceLabel,
      result,
      ip: '10.24.18.32',
      request_no: requestNo(),
      detail,
      before_json: before ? JSON.stringify(toPlain(before)).slice(0, 1800) : '',
      after_json: after ? JSON.stringify(toPlain(after)).slice(0, 1800) : '',
      created_at: nowIso(),
      row_version: 1,
    })
    if (logs.length > 320) logs.length = 320
  }

  private validate(page: PageDefinition, values: Record<string, unknown>, editing: boolean): void {
    const errors: Record<string, string> = {}
    for (const field of page.formFields ?? []) {
      if (editing && field.readonlyOnEdit) continue
      if (field.type === 'readonly') continue
      const value = values[field.key]
      if (field.required && (value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length))) {
        errors[field.key] = `请填写${field.label}`
        continue
      }
      if (value === undefined || value === null || value === '') continue
      if (field.pattern && typeof value === 'string' && !new RegExp(field.pattern).test(value)) {
        errors[field.key] = field.patternMessage ?? `${field.label}格式不正确`
      }
      if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))) {
        errors[field.key] = '请输入有效邮箱地址'
      }
      if (field.type === 'phone' && !/^1[3-9]\d{9}$/.test(String(value))) {
        errors[field.key] = '请输入11位有效手机号'
      }
      if (field.type === 'number' || field.type === 'money') {
        const number = Number(value)
        if (!Number.isFinite(number)) errors[field.key] = `${field.label}必须是数字`
        else if (field.min !== undefined && number < field.min) errors[field.key] = `${field.label}不能小于${field.min}`
        else if (field.max !== undefined && number > field.max) errors[field.key] = `${field.label}不能大于${field.max}`
      }
    }
    if (page.id === 'notification-templates') {
      const allowed = new Set(Array.isArray(values.variables) ? (values.variables as unknown[]).map(String) : [])
      const text = `${String(values.title_template ?? '')} ${String(values.content_template ?? '')}`
      const unknown = Array.from(text.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)).map(match => match[1]!.trim()).filter(name => !allowed.has(name))
      if (unknown.length) errors.content_template = `存在未声明的模板变量：${Array.from(new Set(unknown)).join('、')}`
    }
    if (page.id === 'content-tips' && String(values.status ?? '') === 'online') {
      for (const key of ['title', 'cover_name', 'link_name', 'audience']) {
        if (!String(values[key] ?? '').trim()) {
          const labels: Record<string, string> = { title: '标题', cover_name: '封面素材', link_name: '跳转链接', audience: '适用人群' }
          errors[key] = `上线前必须填写${labels[key]}`
        }
      }
    }
    if (page.id === 'links') {
      const targetType = String(values.target_type ?? '')
      if (targetType === '用户端页面') {
        const targetPage = String(values.target_page ?? '')
        if (!USER_PAGE_OPTIONS.some(option => option.value === targetPage || option.label === targetPage)) {
          errors.target_page = '请选择用户端页面注册表中的有效页面'
        }
      }
      if (targetType === '业务动作' && !String(values.business_action ?? '').trim()) {
        errors.business_action = '业务动作链接必须选择动作'
      }
      if (targetType === '外部链接') {
        const rawUrl = String(values.external_url ?? '')
        try {
          const parsed = new URL(rawUrl)
          if (parsed.protocol !== 'https:') errors.external_url = '外部链接必须使用 HTTPS'
          else if (!/(^|\.)example\.com$|(^|\.)kanghu\.example$/.test(parsed.hostname)) {
            errors.external_url = '外部链接域名不在运营白名单内'
          }
        } catch {
          errors.external_url = '请输入完整的 HTTPS 外部链接'
        }
      }
      if (values.params) {
        try {
          const parsed = JSON.parse(String(values.params)) as Record<string, unknown>
          const allowed = new Set(['source', 'from', 'campaign', 'channel'])
          const unknown = Object.keys(parsed).filter(key => !allowed.has(key))
          if (unknown.length) errors.params = `附加参数包含未登记字段：${unknown.join('、')}`
        } catch {
          errors.params = '附加参数必须是合法 JSON'
        }
      }
    }
    if (Object.keys(errors).length) {
      throw new ApiError('422', '提交内容未通过校验，请检查标注字段', errors)
    }
    const uniqueChecks: Record<string, string> = {
      account: '登录账号',
      code: '编码',
      approval_no: '批准文号',
      barcode: '条形码',
      family_no: '家庭编号',
      plan_no: '计划编号',
    }
    for (const [key, label] of Object.entries(uniqueChecks)) {
      const value = values[key]
      if (!value) continue
      const table = this.table(this.moduleFor(page))
      const duplicate = table.find(row => String(row[key]) === String(value) && String(row.id) !== String(values.__editingId ?? ''))
      if (duplicate) {
        throw new ApiError('422', `${label}已存在，请更换后重试`, { [key]: `${label}已存在` })
      }
    }
  }

  private derive(page: PageDefinition, values: Record<string, unknown>): void {
    const users = this.table('users')
    const families = this.table('families')
    const careTeam = this.table('care_team')
    const drugs = this.table('drugs')
    const regions = this.table('regions')
    if (values.user_id) {
      const user = users.find(item => item.id === values.user_id)
      if (user) {
        values.user_name = user.name
        values.family_id = values.family_id || user.family_id
        values.family_name = user.family_name
        values.region_id = values.region_id || user.region_id
      }
    }
    if (values.family_id) {
      const family = families.find(item => item.id === values.family_id)
      if (family) {
        values.family_name = family.name
        values.region_id = values.region_id || family.region_id
      }
    }
    if (values.owner_user_id) {
      const user = users.find(item => item.id === values.owner_user_id)
      if (user) values.owner_name = user.name
    }
    if (values.owner_id) {
      const owner = careTeam.find(item => item.id === values.owner_id)
      if (owner) values.owner_name = owner.name
    }
    if (values.drug_id) {
      const drug = drugs.find(item => item.id === values.drug_id)
      if (drug) values.drug_name = drug.generic_name
    }
    if (values.region_id) {
      const region = regions.find(item => item.id === values.region_id)
      if (region) {
        values.region_name = region.name
        values.region_path = region.path
      }
    }
    if (values.birth_date) {
      const birth = new Date(String(values.birth_date))
      if (!Number.isNaN(birth.getTime())) values.age = Math.max(0, new Date().getFullYear() - birth.getFullYear())
    }
    if (Array.isArray(values.role_codes)) {
      values.role_names = values.role_codes.map(code => {
        const map: Record<string, string> = {
          super_admin: '超级管理员',
          operations_manager: '运营管理员',
          health_advisor: '健康顾问',
          drug_librarian: '药品库管理员',
          drug_reviewer: '药事审核员',
          support_agent: '客服专员',
          device_ops: '设备运维',
          analyst: '数据分析员',
          auditor: '审计员',
        }
        return map[String(code)] ?? String(code)
      })
    }
    if (page.id === 'drugs' || page.id === 'drug-review') {
      const stock = Number(values.stock ?? 0)
      const safety = Number(values.safety_stock ?? 0)
      values.stock_state = stock <= 0 ? 'out' : stock < safety ? 'low' : 'sufficient'
      values.stock_low = stock < safety
    }
    if (page.id === 'devices' && values.model_name) {
      const model = this.table('device_models').find(item => item.name === values.model_name)
      if (model) values.device_type = values.device_type || model.device_type
    }
    if (page.id === 'regions') {
      const region = values as unknown as AdminRecord
      region.level_label = region.level === 'province' ? '省级' : region.level === 'city' ? '市级' : '区县级'
      const parent = regions.find(item => item.id === region.parent_id)
      region.parent_name = parent ? String(parent.name) : region.parent_name || '—'
      region.path = parent ? `${parent.path} / ${region.name}` : String(region.name)
    }
    if (page.id === 'links' && values.target_page) {
      const target = USER_PAGE_OPTIONS.find(option => option.value === values.target_page || option.label === values.target_page)
      if (target) values.target_page = target.label
    }
    if (page.id === 'membership-orders') {
      const amount = Number(values.amount_fen ?? 0)
      const refund = Number(values.refund_amount_fen ?? 0)
      if (refund > 0 && refund < amount) values.status = 'refund_pending'
    }
  }

  private applyDefaults(page: PageDefinition, values: Record<string, unknown>): void {
    for (const field of page.formFields ?? []) {
      if (values[field.key] !== undefined && values[field.key] !== '') continue
      if (field.defaultValue !== undefined) values[field.key] = toPlain(field.defaultValue)
      else if (field.type === 'switch') values[field.key] = false
      else if (field.type === 'multiselect') values[field.key] = []
    }
  }

  private decorateNew(page: PageDefinition, input: Record<string, unknown>): AdminRecord {
    const values = toPlain(input)
    this.applyDefaults(page, values)
    const now = nowIso()
    const module = this.moduleFor(page)
    const record: AdminRecord = {
      id: this.nextId(module),
      ...values,
      created_at: now,
      updated_at: now,
      row_version: 1,
    }
    if (page.id === 'users' && !record.user_no) record.user_no = businessNo('KH')
    if (page.id === 'families' && !record.family_no) record.family_no = businessNo('JT')
    if (page.id === 'medication-plans' && !record.plan_no) record.plan_no = businessNo('YY')
    if (page.id === 'devices' && !record.device_no) record.device_no = businessNo('SB')
    if (page.id === 'drugs' && !record.drug_no) record.drug_no = businessNo('YP')
    if (page.id === 'messages' && !record.message_no) record.message_no = businessNo('XX')
    if (page.id === 'health-reports' && !record.report_no) record.report_no = businessNo('BG')
    if (page.id === 'admin-users' && !record.admin_no) record.admin_no = businessNo('GL')
    if (page.id === 'membership-plans' && !record.plan_no) record.plan_no = businessNo('HY')
    if (page.id === 'membership-plans' && record.version === undefined) record.version = 1
    if (page.id === 'roles') {
      record.permission_codes = []
      record.permission_count = 0
      record.member_count = 0
      record.is_builtin = false
    }
    if (page.id === 'admin-users') {
      record.last_login_at = ''
      record.login_history = []
    }
    if (page.id === 'content-tips') record.views = 0
    if (page.id === 'content-faq') record.views = 0
    if (page.id === 'messages') record.delivered = 0
    if (page.id === 'notification-templates') record.version = 1
    if (page.id === 'alert-rules') record.version = 1
    if (page.id === 'membership-orders') record.status = record.status ?? 'pending'
    if (page.id === 'health-reports') {
      record.status = record.status ?? 'generating'
      record.generated_at = record.generated_at ?? ''
      record.sent_at = record.sent_at ?? ''
      record.failure_reason = record.failure_reason ?? ''
      record.asset_name = record.asset_name ?? ''
    }
    this.derive(page, record)
    return record
  }

  create(page: PageDefinition, input: Record<string, unknown>, actor: string): AdminRecord {
    if (page.readOnly) throw new ApiError('409', '当前页面为只读，不能新增记录')
    this.validate(page, input, false)
    const record = this.decorateNew(page, input)
    const table = this.table(this.moduleFor(page))
    table.unshift(record)
    this.syncDerived()
    this.recordAudit(actor, page.title, '新增', String(record.name ?? record.title ?? record.generic_name ?? record.plan_no ?? record.id), '成功', null, record, `新增${page.title}`)
    return { ...record }
  }

  update(page: PageDefinition, id: string, input: Record<string, unknown>, actor: string): AdminRecord {
    if (page.readOnly) throw new ApiError('409', '当前页面为只读，不能编辑记录')
    const table = this.table(this.moduleFor(page))
    const index = table.findIndex(row => String(row.id) === id)
    if (index < 0) throw new ApiError('404', '目标记录不存在或已归档')
    const before: AdminRecord = { ...(table[index] as AdminRecord) }
    const values = toPlain(input)
    const expectedVersion = Number(values.__rowVersion ?? values.row_version ?? before.row_version ?? 1)
    if (expectedVersion !== Number(before.row_version ?? 1)) {
      throw new ApiError('409', '记录已被其他操作更新，请刷新后重试', undefined, {
        expectedVersion,
        currentVersion: Number(before.row_version ?? 1),
      })
    }
    const candidate: AdminRecord = { ...before, ...values }
    this.validate(page, { ...candidate, __editingId: id }, true)
    if (page.id === 'admin-users') {
      const beforeRoles = Array.isArray(before.role_codes) ? before.role_codes.map(String) : []
      const nextRoles = Array.isArray(candidate.role_codes) ? candidate.role_codes.map(String) : []
      if (beforeRoles.includes('super_admin') && (!nextRoles.includes('super_admin') || candidate.status !== 'active')) {
        const otherSupers = this.table('admins').filter(item =>
          String(item.id) !== id
          && Array.isArray(item.role_codes)
          && (item.role_codes as string[]).includes('super_admin')
          && item.status === 'active',
        )
        if (!otherSupers.length) throw new ApiError('409', '最后一名超级管理员不可停用或移除超级管理员角色')
      }
    }
    const next: AdminRecord = { ...candidate, updated_at: nowIso(), row_version: Number(before.row_version ?? 1) + 1 }
    if (page.id === 'membership-plans' && Number(candidate.price_fen) !== Number(before.price_fen)) {
      next.version = Number(before.version ?? 1) + 1
    }
    if (page.id === 'alert-rules') {
      const thresholdChanged = ['lower_bound', 'upper_bound', 'duration_minutes', 'severity', 'audience', 'channels']
        .some(key => JSON.stringify(candidate[key]) !== JSON.stringify(before[key]))
      if (thresholdChanged) next.version = Number(before.version ?? 1) + 1
    }
    delete next.__rowVersion
    this.derive(page, next)
    table[index] = next
    this.syncDerived()
    this.recordAudit(actor, page.title, '编辑', String(next.name ?? next.title ?? next.generic_name ?? next.plan_no ?? id), '成功', before, next, `编辑${page.title}`)
    return { ...next }
  }

  remove(page: PageDefinition, id: string, actor: string): ActionResult {
    if (page.readOnly) throw new ApiError('409', '当前页面为只读，不能删除记录')
    const table = this.table(this.moduleFor(page))
    const index = table.findIndex(row => String(row.id) === id)
    if (index < 0) throw new ApiError('404', '目标记录不存在或已归档')
    const before: AdminRecord = { ...(table[index] as AdminRecord) }
    if (page.id === 'regions') {
      const refs = this.regionReferences(id)
      if (refs.length) this.throwConflict('region', before, 'regions', refs)
    }
    if (page.id === 'membership-plans' && Number(before.sales_count ?? 0) > 0) {
      throw new ApiError('409', `该方案已有 ${Number(before.sales_count ?? 0)} 笔购买记录，不能删除；请下线并保留历史订单快照`)
    }
    if (page.id === 'notification-templates') {
      const refs = this.table('messages').filter(item => item.template_name === before.name)
      if (refs.length) {
        this.throwConflict('templates', before, 'notification_templates', [{ module: 'messages', label: '通知消息', count: refs.length }])
      }
    }
    if (page.id === 'content-tips' && before.status !== 'offline') {
      throw new ApiError('409', '仅已下线的健康贴士可以删除，请先下线并确认没有用户端投放引用')
    }
    if (page.id === 'content-faq' && before.status !== 'offline') {
      throw new ApiError('409', '仅已下线的帮助问题可以删除，请先下线并保留历史访问统计')
    }
    if (page.id === 'links') {
      const usage = this.table('links').find(item => String(item.id) === id)?.usage_count
      if (Number(usage ?? 0) > 0) {
        this.throwConflict('links', before, 'links', [{ module: 'links', label: '引用位置', count: Number(usage ?? 0) }])
      }
    }
    if (page.id === 'assets') {
      const refs = this.assetReferences(String(before.name))
      if (refs.length) this.throwConflict('assets', before, 'assets', refs)
    }
    if (page.id === 'admin-users' && before.role_codes && (before.role_codes as string[]).includes('super_admin')) {
      const supers = this.table('admins').filter(item => Array.isArray(item.role_codes) && (item.role_codes as string[]).includes('super_admin') && item.status === 'active')
      if (supers.length <= 1) throw new ApiError('409', '最后一名超级管理员不可删除')
    }
    table.splice(index, 1)
    this.syncDerived()
    this.recordAudit(actor, page.title, '删除', String(before.name ?? before.title ?? id), '成功', before, null, `删除${page.title}`)
    return { ok: true, message: `已删除${page.title}记录`, succeeded: 1 }
  }

  private regionReferences(regionId: string): Array<{ module: string; label: string; count: number }> {
    const refs: Array<{ module: string; label: string; count: number }> = []
    const users = this.table('users').filter(item => item.region_id === regionId).length
    const families = this.table('families').filter(item => item.region_id === regionId).length
    const devices = this.table('devices').filter(item => item.region_id === regionId).length
    const region = this.table('regions').find(item => String(item.id) === regionId)
    const admins = region && region.level === 'district'
      ? this.table('admins').filter(item => item.data_scope !== 'all' && String(item.region_scope ?? '').includes(String(region.name))).length
      : 0
    if (users) refs.push({ module: 'users', label: '用户', count: users })
    if (families) refs.push({ module: 'families', label: '家庭', count: families })
    if (devices) refs.push({ module: 'devices', label: '设备', count: devices })
    if (admins) refs.push({ module: 'admins', label: '管理员数据范围', count: admins })
    return refs
  }

  private throwConflict(
    resource: 'region' | 'regions' | 'links' | 'assets' | 'masters' | 'templates' | 'content' | 'membership_plans',
    record: AdminRecord,
    entity: string,
    references: Array<{ module: string; label: string; count: number }>,
  ): never {
    const detail = references.map(item => `${item.label} ${item.count} 项`).join('、')
    throw new BusinessConflict(`该${record.name ?? record.title ?? '记录'}仍被${detail}引用，请先完成迁移`, {
      entity,
      entityId: String(record.id),
      entityName: String(record.name ?? record.title ?? record.id),
      resource,
      references,
      message: detail,
    })
  }

  executeRowAction(
    page: PageDefinition,
    rowId: string,
    actionKey: string,
    input: Record<string, unknown>,
    actor: string,
  ): ActionOutcome {
    const table = this.table(this.moduleFor(page))
    const record = table.find(item => String(item.id) === rowId)
    if (!record) throw new ApiError('404', '目标记录不存在或已归档')
    const before = { ...record }
    const values = toPlain(input)
    const now = nowIso()
    const patch = (changes: Record<string, unknown>): void => {
      Object.assign(record, changes, { updated_at: now, row_version: Number(record.row_version ?? 1) + 1 })
      this.syncDerived()
    }
    const done = (action: string, message: string, detail?: string): ActionOutcome => {
      this.recordAudit(actor, page.title, action, String(record.name ?? record.title ?? record.plan_no ?? record.id), '成功', before, record, detail ?? message)
      return { message, row: { ...record } }
    }
    switch (actionKey) {
      case 'status-toggle':
      case 'toggle':
      case 'toggle-rule':
      case 'toggle-team': {
        const isDisabled = record.status === 'disabled'
        if (!isDisabled && page.id === 'users' && !String(values.reason ?? '').trim()) {
          throw new ApiError('422', '停用用户必须填写原因', { reason: '请填写停用原因' })
        }
        if (!isDisabled && page.id === 'roles' && record.is_builtin && record.code === 'super_admin') {
          throw new ApiError('409', '内置超级管理员角色不可停用')
        }
        if (!isDisabled && page.id === 'admin-users' && Array.isArray(record.role_codes) && (record.role_codes as string[]).includes('super_admin')) {
          const supers = this.table('admins').filter(item => Array.isArray(item.role_codes) && (item.role_codes as string[]).includes('super_admin') && item.status === 'active')
          if (supers.length <= 1) throw new ApiError('409', '最后一名超级管理员不可停用')
        }
        if (!isDisabled && actionKey === 'toggle-team') {
          const openAlerts = this.table('alerts').filter(alert => String(alert.owner_id) === String(record.id) && ['pending', 'processing'].includes(String(alert.status))).length
          if (Number(record.family_count ?? 0) > 0 || openAlerts > 0) {
            throw new ApiError('409', `该成员仍负责 ${record.family_count ?? 0} 户家庭、${openAlerts} 条未关闭预警，请先转交服务`)
          }
        }
        if (!isDisabled && page.id === 'notification-templates') {
          const scheduled = this.table('messages').filter(item =>
            item.template_name === record.name && ['scheduled', 'sending'].includes(String(item.status)),
          )
          if (scheduled.length) {
            throw new ApiError('409', `该模板仍被 ${scheduled.length} 条待发送消息引用，请先取消或发送完成后再停用`)
          }
        }
        if (!isDisabled && page.id === 'links' && Number(record.usage_count ?? 0) > 0) {
          throw new ApiError('409', `该链接仍被 ${record.usage_count} 处引用，请先迁移引用后再停用`)
        }
        patch({ status: isDisabled ? 'active' : 'disabled', disabled_reason: isDisabled ? '' : values.reason ?? '' })
        return done(isDisabled ? '启用' : '停用', isDisabled ? '已启用，权限即时生效' : '已停用，相关会话立即失效')
      }
      case 'disable-region': {
        const refs = this.regionReferences(String(record.id))
        if (refs.length) this.throwConflict('region', record, 'regions', refs)
        patch({ status: 'disabled', disabled_reason: values.reason ?? '' })
        return done('停用', '区域已停用，表单和筛选不再展示该区域')
      }
      case 'enable-region': {
        patch({ status: 'active' })
        return done('启用', '区域已启用，可被表单和筛选选择')
      }
      case 'migrate-region': {
        const targetId = String(values.target_region_id ?? values.region_id ?? '')
        const target = this.table('regions').find(item => item.id === targetId)
        if (!target || target.id === record.id) throw new ApiError('422', '请选择不同的启用目标区域', { target_region_id: '目标区域无效' })
        if (target.status !== 'active') throw new ApiError('422', '目标区域已停用，不能接收资源')
        if (page.id === 'families') {
          patch({ region_id: target.id, region_name: target.name, region_path: target.path })
          return done('迁移区域', `家庭已迁移至${target.name}，地址与设备归属保持同步`)
        }
        let moved = 0
        for (const module of ['users', 'families', 'devices']) {
          for (const item of this.table(module)) {
            if (item.region_id === record.id) {
              item.region_id = target.id
              item.region_name = target.name
              item.region_path = target.path
              moved += 1
            }
          }
        }
        for (const user of this.table('users')) {
          if (record.level === 'province' && String(user.region_path ?? '').startsWith(String(record.path))) {
            user.region_path = String(user.region_path).replace(String(record.path), String(target.path))
            moved += 1
          }
        }
        for (const admin of this.table('admins')) {
          if (record.level !== 'district' || admin.data_scope === 'all') continue
          const scope = String(admin.region_scope ?? '')
          if (!scope.includes(String(record.name))) continue
          admin.region_scope = scope.replace(String(record.name), String(target.name))
          admin.updated_at = now
          admin.row_version = Number(admin.row_version ?? 1) + 1
          moved += 1
        }
        patch({ status: record.status === 'disabled' ? 'active' : record.status })
        this.syncDerived()
        return done('迁移', `已将 ${moved} 项关联资源迁移至${target.name}`, `区域资源迁移：${record.name} → ${target.name}`)
      }
      case 'pause': {
        if (!String(values.reason ?? '').trim()) throw new ApiError('422', '暂停计划必须填写原因', { reason: '请填写暂停原因' })
        patch({ status: 'paused', pause_reason: values.reason ?? '' })
        return done('暂停', '计划已暂停，恢复后从下一个提醒时间继续')
      }
      case 'cancel-account': {
        const openOrders = this.table('orders').filter(order => String(order.user_id) === String(record.id) && ['pending', 'refund_pending'].includes(String(order.status)))
        const openTickets = this.table('tickets').filter(ticket => String(ticket.user_id) === String(record.id) && !['resolved', 'closed'].includes(String(ticket.status)))
        if (openOrders.length || openTickets.length) {
          throw new ApiError('409', `该用户仍有 ${openOrders.length} 个未完成订单、${openTickets.length} 个在办工单，需处理完成后才能注销`)
        }
        if (!String(values.reason ?? '').trim()) throw new ApiError('422', '注销用户必须填写原因', { reason: '请填写注销原因' })
        patch({ status: 'cancelled', cancel_reason: values.reason ?? '', cancelled_at: now })
        return done('注销', '用户账号已注销，历史健康数据按法定期限保留')
      }
      case 'resume': {
        patch({ status: 'active', pause_reason: '' })
        return done('恢复', '计划已恢复，已重新生成提醒')
      }
      case 'remind': {
        patch({ next_remind_at: now, last_reminded_at: now, remind_count: Number(record.remind_count ?? 0) + 1 })
        return done('发送提醒', `已向${record.user_name ?? '用户'}发送用药提醒`)
      }
      case 'correct': {
        patch({ status: values.status ?? record.status, taken_at: values.taken_at ?? record.taken_at, note: values.note ?? record.note, corrected_from: record.status })
        return done('更正记录', '记录已更正，原值保留在操作日志中')
      }
      case 'create-alert': {
        const alerts = this.table('alerts')
        const alert = {
          id: this.nextId('alerts'),
          alert_no: businessNo('YJ'),
          user_id: record.user_id,
          user_name: record.user_name,
          family_id: record.family_id,
          family_name: record.family_name,
          rule_name: '手动生成预警',
          alert_type: record.metric_type,
          metric_value: record.value_display,
          reference_range: record.reference_range,
          severity: record.severity === '高' ? '高' : '中',
          status: 'pending',
          owner_id: '',
          owner_name: '待指派',
          triggered_at: now,
          sla_due_at: now,
          closed_at: '',
          close_reason: '',
          created_at: now,
          updated_at: now,
          row_version: 1,
        }
        alerts.unshift(alert)
        return done('生成预警', `已生成预警 ${alert.alert_no}，进入待处理队列`)
      }
      case 'assign': {
        const owner = this.table('care_team').find(item => item.id === values.owner_id)
        if (!owner) throw new ApiError('422', '请选择有效的跟进人', { owner_id: '跟进人无效' })
        if (owner.status !== 'active') throw new ApiError('422', '只能指派给启用中的医护成员', { owner_id: '该成员已停用' })
        patch({ owner_id: owner.id, owner_name: owner.name, status: 'processing', assigned_at: now })
        return done('指派', `已指派给${owner.name}，处理状态更新为处理中`)
      }
      case 'contact-family': {
        patch({ contact_note: values.note ?? '', status: 'processing' })
        return done('联系家属', '沟通记录已保存，预警状态更新为处理中')
      }
      case 'generate-report': {
        const reports = this.table('reports')
        const report = {
          id: this.nextId('reports'),
          report_no: businessNo('BG'),
          user_id: record.user_id,
          user_name: record.user_name,
          family_name: record.family_name,
          period_start: formatDate(now),
          period_end: formatDate(now),
          report_type: '周报',
          template_name: '家庭健康周报',
          status: 'completed',
          generated_at: now,
          sent_at: '',
          failure_reason: '',
          asset_name: '',
          created_at: now,
          updated_at: now,
          row_version: 1,
        }
        reports.unshift(report)
        return done('生成报告', `已为${record.user_name ?? '用户'}生成健康报告 ${report.report_no}`)
      }
      case 'handle': {
        patch({ status: 'resolved', resolution: values.resolution ?? values.note ?? '', closed_at: now })
        return done('处理', '已标记处理完成，处理说明已留痕')
      }
      case 'close': {
        const reason = String(values.close_reason ?? values.reason ?? values.resolution ?? '').trim()
        if ((page.id === 'health-alerts' || page.id === 'support-tickets') && !reason) {
          throw new ApiError('422', '关闭记录必须填写处理结论', { close_reason: '请填写关闭原因或处理结论' })
        }
        patch({ status: 'closed', close_reason: reason, closed_at: now })
        return done('关闭', '已关闭并进入闭环，不再参与 SLA 计时')
      }
      case 'test-rule': {
        const sample = Number(values.sample_value ?? record.upper_bound ?? 0)
        const hit = sample > Number(record.upper_bound ?? 0) || sample < Number(record.lower_bound ?? 0)
        return {
          message: hit ? '样例命中规则，将生成预警' : '样例未命中规则',
          info: {
            title: '规则样例测试',
            lines: [
              { label: '样例指标值', value: String(sample) },
              { label: '规则区间', value: `${record.lower_bound} – ${record.upper_bound}` },
              { label: '持续时长', value: `${record.duration_minutes} 分钟` },
              { label: '推送渠道', value: Array.isArray(record.channels) ? record.channels.join('、') : String(record.channels ?? '') },
              { label: '测试结果', value: hit ? '命中，生成模拟预警' : '未命中' },
            ],
            note: '样例测试不会触达真实用户，也不会修改规则版本。',
          },
        }
      }
      case 'version-history': {
        const version = Number(record.version ?? 1)
        return {
          message: '已加载版本历史',
          info: {
            title: `${record.name ?? '规则'} · 版本历史`,
            lines: Array.from({ length: Math.min(version, 5) }, (_, index) => ({
              label: `v${version - index}`,
              value: `${index === 0 ? '当前生效' : '历史版本'} · ${formatDateTime(record.updated_at)} · 阈值 ${record.lower_bound}–${record.upper_bound}`,
            })),
            note: '历史预警保留触发时的规则快照，修改规则不会追溯改写历史。',
          },
        }
      }
      case 'timeline': {
        return {
          message: '已加载计划时间线',
          info: {
            title: `${record.plan_no ?? '计划'} · 时间线`,
            lines: [
              { label: '计划开始', value: formatDate(record.start_date) },
              { label: '计划结束', value: formatDate(record.end_date) },
              { label: '下次提醒', value: formatDateTime(record.next_remind_at) },
              { label: '当前状态', value: String(page.statusMap?.[String(record.status)]?.label ?? record.status ?? '—') },
              { label: '最近更新', value: formatDateTime(record.updated_at) },
            ],
            note: '时间线只展示当前内存数据，正式开发将读取服务端提醒记录。',
          },
        }
      }
      case 'workload': {
        return {
          message: '已加载工作量',
          info: {
            title: `${record.name ?? '成员'} · 服务工作量`,
            lines: [
              { label: '负责家庭', value: `${record.family_count ?? 0} 户` },
              { label: '工作量', value: `${record.workload ?? 0}%` },
              { label: '服务区域', value: String(record.region_name ?? '—') },
              { label: '在岗状态', value: record.status === 'active' ? '启用' : '停用' },
            ],
            note: '停用前需转交负责家庭与待处理预警。',
          },
        }
      }
      case 'members': {
        const members = this.table('admins').filter(item => Array.isArray(item.role_codes) && (item.role_codes as string[]).includes(String(record.code)))
        return {
          message: `该角色下有 ${members.length} 名管理员`,
          info: {
            title: `${record.name ?? '角色'} · 成员`,
            lines: members.length
              ? members.map(item => ({ label: String(item.name), value: `${item.account} · ${item.status === 'active' ? '启用' : '停用'}` }))
              : [{ label: '暂无成员', value: '可通过管理员与组织分配该角色' }],
          },
        }
      }
      case 'assign-families': {
        const familyIds = Array.isArray(values.family_ids) ? values.family_ids as string[] : []
        if (!familyIds.length) throw new ApiError('422', '请至少选择一个家庭', { family_ids: '请选择家庭' })
        patch({ family_count: familyIds.length })
        return done('分配家庭', `已分配 ${familyIds.length} 户家庭，服务范围已更新`)
      }
      case 'transfer-team': {
        const target = this.table('care_team').find(item => String(item.id) === String(values.target_id) && item.status === 'active')
        if (!target || String(target.id) === String(record.id)) {
          throw new ApiError('422', '请选择另一位启用中的医护成员', { target_id: '接收成员无效' })
        }
        let alertCount = 0
        let planCount = 0
        for (const alert of this.table('alerts')) {
          if (String(alert.owner_id) === String(record.id) && ['pending', 'processing'].includes(String(alert.status))) {
            alert.owner_id = target.id
            alert.owner_name = target.name
            alertCount += 1
          }
        }
        for (const plan of this.table('medication_plans')) {
          if (String(plan.owner_id) === String(record.id)) {
            plan.owner_id = target.id
            plan.owner_name = target.name
            planCount += 1
          }
        }
        target.family_count = Number(target.family_count ?? 0) + Number(record.family_count ?? 0)
        patch({ family_count: 0 })
        return done('转交服务', `已将 ${record.family_count ?? 0} 户家庭、${alertCount} 条预警、${planCount} 个计划转交至${target.name}`, String(values.note ?? ''))
      }
      case 'bind': {
        const user = this.table('users').find(item => item.id === values.user_id)
        if (!user) throw new ApiError('422', '请选择有效用户', { user_id: '用户无效' })
        patch({ user_id: user.id, user_name: user.name, family_id: values.family_id ?? user.family_id, family_name: user.family_name, region_id: user.region_id, region_name: user.region_name, status: 'online', bound_at: now })
        return done('绑定设备', `设备已绑定至${user.name}，开始接收健康数据`)
      }
      case 'unbind': {
        if (!String(values.reason ?? '').trim()) throw new ApiError('422', '解绑设备必须填写原因', { reason: '请填写解绑原因' })
        patch({ user_id: '', user_name: '未绑定', family_id: '', family_name: '', status: 'unbound', bound_at: '', unbind_reason: String(values.reason) })
        return done('解绑设备', '设备已解绑，健康数据上报停止')
      }
      case 'sync': {
        if (record.status !== 'online') throw new ApiError('409', '离线或故障设备不能执行命令')
        patch({ last_report_at: now })
        return done('下发同步', '同步命令已下发，最近上报时间已刷新')
      }
      case 'firmware': {
        if (record.status !== 'online') throw new ApiError('409', '离线或故障设备不能执行固件升级')
        const current = String(record.firmware ?? 'v1.0.0')
        const match = /v?(\d+)\.(\d+)\.(\d+)/.exec(current)
        const next = match ? `v${match[1]}.${match[2]}.${Number(match[3]) + 1}` : 'v1.0.1'
        patch({ firmware: next, last_report_at: now })
        return done('固件升级', `固件已从 ${current} 升级至 ${next}`)
      }
      case 'fault': {
        patch({ status: 'fault', fault_note: values.fault_note ?? '' })
        return done('标记故障', '设备已标记故障，相关健康监测会提示数据可能中断')
      }
      case 'resolve': {
        patch({ status: 'online', fault_note: '', last_report_at: now })
        return done('恢复设备', '设备已恢复在线，故障标记已清除')
      }
      case 'approve': {
        patch({ review_status: 'approved', approved_at: now, reject_reason: '' })
        return done('审核通过', '药品已通过药事审核，可被用药计划引用')
      }
      case 'reject': {
        if (!String(values.reason ?? '').trim()) throw new ApiError('422', '驳回药品必须填写原因', { reason: '请填写驳回原因' })
        patch({ review_status: 'rejected', reject_reason: values.reason ?? '', reviewed_at: now })
        return done('驳回', '已驳回并记录原因，提交人可修改后重新提交')
      }
      case 'offline': {
        if (page.id === 'drugs' || page.id === 'drug-review') {
          patch({ review_status: 'offline', offline_reason: values.reason ?? '' })
          return done('下架', '药品已下架，已生效的用药计划不被阻断')
        }
        patch({ status: 'offline' })
        return done('下线', '内容已下线，用户端不再展示')
      }
      case 'online':
      case 'publish': {
        patch({ status: page.id === 'content-faq' ? 'published' : 'online' })
        return done('上线', '内容已上线，用户端即时可见')
      }
      case 'submit-review': {
        patch({ review_status: 'pending', submitted_at: now })
        return done('提交审核', '已提交药事审核，审核前不可新增引用')
      }
      case 'adjust-stock': {
        const stock = Number(values.stock ?? record.stock ?? 0)
        if (!Number.isFinite(stock) || stock < 0) throw new ApiError('422', '库存必须是非负数字', { stock: '库存必须是非负数字' })
        patch({ stock, stock_low: stock < Number(record.safety_stock ?? 0), stock_note: values.reason ?? '' })
        return done('调整库存', `库存已调整为 ${stock}，低库存提醒同步更新`)
      }
      case 'reset-password': {
        const temp = `Kh${Math.random().toString(36).slice(2, 8).toUpperCase()}`
        patch({ password_reset_at: now, must_change_password: true })
        return {
          message: '临时密码已生成，仅展示一次',
          tempPassword: temp,
          row: { ...record },
        }
      }
      case 'force-logout': {
        patch({ force_logout_at: now })
        return done('强制下线', '已清除该管理员全部会话，下一次请求将要求重新登录')
      }
      case 'copy': {
        const copy: AdminRecord = {
          ...toPlain(record),
          id: this.nextId(this.moduleFor(page)),
          created_at: now,
          updated_at: now,
          row_version: 1,
        }
        if (page.id === 'messages') copy.message_no = businessNo('XX')
        if (page.id === 'membership-plans') {
          copy.plan_no = businessNo('HY')
          copy.name = `${record.name}（副本）`
          copy.sales_count = 0
          copy.status = 'offline'
        }
        if (page.id === 'roles') {
          copy.code = `${record.code}_copy`
          copy.name = `${record.name}（副本）`
          copy.is_builtin = false
          copy.member_count = 0
        }
        table.unshift(copy)
        this.recordAudit(actor, page.title, '复制', String(copy.name ?? copy.title ?? copy.id), '成功', null, copy, '复制记录')
        return { message: '已复制为新的草稿记录', row: copy }
      }
      case 'send': {
        if (page.id === 'health-reports') {
          patch({ status: 'sent', sent_at: now })
          return done('发送', '报告已发送给用户与家庭守护人')
        }
        patch({ status: 'sent', sent_at: now, delivered: Number(record.audience_label?.toString().replace(/[^\d]/g, '') ?? 0) || Number(record.delivered ?? 0) })
        return done('发送', '消息已进入发送队列并完成送达统计')
      }
      case 'cancel': {
        patch({ status: 'cancelled', cancelled_at: now })
        return done('取消发送', '待发送消息已取消，不会触达用户')
      }
      case 'retry': {
        patch({ status: 'completed', generated_at: now, failure_reason: '' })
        return done('重试', '报告已重新生成成功')
      }
      case 'generate': {
        patch({ status: 'completed', generated_at: now, failure_reason: '' })
        return done('重新生成', '报告已重新生成，可继续发送')
      }
      case 'download': {
        return {
          message: '报告文件已准备下载',
          download: {
            filename: `${record.report_no ?? '健康报告'}.txt`,
            content: `康护健康 · ${record.report_type ?? '健康报告'}\n用户：${record.user_name}\n周期：${formatDate(record.period_start)} 至 ${formatDate(record.period_end)}\n模板：${record.template_name}\n状态：${record.status}\n\n本文件由管理后台原型内存数据生成，不代表正式医疗报告。`,
          },
        }
      }
      case 'test-send': {
        return { message: `测试消息已发送至当前管理员${actor}，未触达真实用户` }
      }
      case 'refund': {
        if (!['paid', 'refund_pending'].includes(String(record.status))) {
          throw new ApiError('409', '只有已支付或退款处理中的订单可以退款')
        }
        const amount = Number(record.amount_fen ?? 0)
        const requested = values.refund_amount_fen === undefined ? amount : Number(values.refund_amount_fen)
        if (!Number.isFinite(requested) || requested <= 0) throw new ApiError('422', '退款金额必须大于 0')
        if (requested > amount) throw new ApiError('422', '退款金额不能超过订单实付金额')
        patch({ status: 'refunded', refund_amount_fen: requested, refund_reason: values.refund_reason ?? '', refunded_at: now })
        return done('退款', '退款已登记，会员权益按退款规则回收')
      }
      case 'create-ticket': {
        const tickets = this.table('tickets')
        const ticket = {
          id: this.nextId('tickets'),
          ticket_no: businessNo('GD'),
          title: page.id === 'membership-orders' ? `会员订单售后 · ${record.order_no}` : `用户反馈转工单 · ${record.feedback_no ?? record.id}`,
          user_name: record.user_name ?? '用户',
          family_name: record.family_name ?? '',
          type: page.id === 'membership-orders' ? '会员支付' : '服务投诉',
          priority: '中',
          status: 'pending',
          assignee_name: '',
          channel: '管理后台',
          description: `由${page.title}转入，请跟进处理。`,
          sla_due_at: now,
          created_at: now,
          updated_at: now,
          row_version: 1,
        }
        tickets.unshift(ticket)
        if (page.id === 'support-feedback') patch({ ticket_no: ticket.ticket_no, status: 'processing' })
        return { message: `已创建工单 ${ticket.ticket_no}`, row: { ...record } }
      }
      case 'reply': {
        if (page.id === 'support-tickets') {
          if (!String(values.reply ?? '').trim()) throw new ApiError('422', '回复内容不能为空', { reply: '请填写回复内容' })
          patch({ status: 'processing', last_reply: values.reply ?? '', replied_at: now })
          return done('回复用户', '回复已通过站内信发送，工单保持处理中')
        }
        patch({ status: 'replied', reply: values.reply ?? '', replied_at: now })
        return done('回复', '回复已通过站内信发送给用户')
      }
      case 'priority': {
        const priority = String(values.priority ?? '')
        if (!['低', '中', '高', '紧急'].includes(priority)) throw new ApiError('422', '请选择有效的工单优先级', { priority: '优先级无效' })
        patch({ priority, priority_updated_at: now })
        return done('调整优先级', `工单优先级已调整为${priority}`)
      }
      case 'escalate': {
        patch({ priority: '紧急', escalated_at: now, status: record.status === 'pending' ? 'assigned' : record.status })
        return done('升级工单', '工单已升级为紧急并进入客服主管队列')
      }
      case 'risk-recompute': {
        const score = (Array.isArray(record.tags) ? record.tags.length : 0) * 2 + Number(record.open_alerts ?? 0)
        const level = score >= 4 ? '高风险' : score >= 2 ? '中风险' : '低风险'
        patch({ risk_level: level, risk_score: score, risk_computed_at: now })
        return done('重算风险', `风险等级已重算为${level}`)
      }
      case 'archive': {
        const refs = this.assetReferences(String(record.name))
        if (refs.length) this.throwConflict('assets', record, 'assets', refs)
        patch({ status: 'archived', archived_at: now })
        return done('归档', '素材已归档，不再出现在选择器中')
      }
      case 'replace': {
        patch({ replaced_at: now, version: Number(record.version ?? 1) + 1 })
        return done('替换素材', '素材已替换，原引用关系保留并记录新版本')
      }
      case 'add-member': {
        patch({ member_count: Number(record.member_count ?? 0) + 1, guardian_count: Number(record.guardian_count ?? 0) + 1 })
        return done('新增成员', `已新增家庭成员${values.name ? `：${values.name}` : ''}`)
      }
      case 'remove-member': {
        const members = this.table('users').filter(user => String(user.family_id) === String(record.id))
        if (Number(record.guardian_count ?? 0) <= 1 || members.length <= 1) {
          throw new ApiError('409', '最后一名家庭守护人不可移除，请先指定新的守护人')
        }
        const target = values.user_id
          ? members.find(user => String(user.id) === String(values.user_id))
          : members[members.length - 1]
        if (!target) throw new ApiError('422', '所选用户不属于该家庭，无法移除', { user_id: '请选择当前家庭成员' })
        if (target) {
          target.family_id = ''
          target.family_name = ''
          target.family_role = ''
          target.consent_scope = []
          target.updated_at = nowIso()
          target.row_version = Number(target.row_version ?? 1) + 1
        }
        patch({ member_count: Math.max(1, members.length - 1), guardian_count: Math.max(1, Number(record.guardian_count ?? 1) - 1) })
        return done('移除成员', `已移除 ${target?.name ?? '1 名成员'}，家庭守护关系与授权已同步更新`, String(values.note ?? ''))
      }
      case 'tags': {
        patch({ tags: Array.isArray(values.tags) ? values.tags : record.tags })
        return done('编辑标签', '健康标签已更新，消息受众与随访筛选同步变化')
      }
      case 'open-device': {
        return { message: '正在打开关联设备', navigate: `#/devices?keyword=${encodeURIComponent(String(record.device_name ?? ''))}` }
      }
      default:
        throw new ApiError('422', '该操作尚未定义可执行的原型规则，请检查页面定义')
    }
  }

  private assetReferences(assetName: string): Array<{ module: string; label: string; count: number }> {
    const refs: Array<{ module: string; label: string; count: number }> = []
    for (const [module, fields] of Object.entries({
      drugs: ['image_name'],
      tips: ['cover_name'],
      membership_plans: ['cover_name'],
      reports: ['asset_name'],
    })) {
      let count = 0
      for (const field of fields) count += this.table(module).filter(item => item[field] === assetName).length
      if (count) refs.push({ module, label: module === 'drugs' ? '药品' : module === 'tips' ? '健康贴士' : module === 'membership_plans' ? '会员方案' : '健康报告', count })
    }
    return refs
  }

  executeBatch(
    page: PageDefinition,
    rows: AdminRecord[],
    actionKey: string,
    input: Record<string, unknown>,
    actor: string,
  ): ActionResult {
    const values = toPlain(input)
    const table = this.table(this.moduleFor(page))
    const failed: Array<{ id: string; label: string; message: string }> = []
    let succeeded = 0
    const patchMany = (changes: (row: AdminRecord) => Record<string, unknown> | null): void => {
      for (const row of rows) {
        const target = table.find(item => String(item.id) === String(row.id))
        if (!target) {
          failed.push({ id: String(row.id), label: String(row.name ?? row.id), message: '记录已不存在' })
          continue
        }
        const changesValue = changes(target)
        if (!changesValue) {
          failed.push({ id: String(row.id), label: String(target.name ?? target.title ?? target.id), message: '当前状态不允许该操作' })
          continue
        }
        Object.assign(target, changesValue, { updated_at: nowIso(), row_version: Number(target.row_version ?? 1) + 1 })
        succeeded += 1
      }
      this.syncDerived()
    }
    if (actionKey === 'batch-disable') {
      patchMany(row => row.status === 'disabled' ? null : { status: 'disabled', disabled_reason: values.reason ?? '' })
    } else if (actionKey === 'batch-close') {
      patchMany(row => row.status === 'closed' ? null : { status: 'closed', closed_at: nowIso(), close_reason: values.reason ?? '' })
    } else if (actionKey === 'batch-send') {
      patchMany(row => row.status === 'sent' ? null : { status: 'sent', sent_at: nowIso() })
    } else if (actionKey === 'batch-cancel') {
      patchMany(row => ['sent', 'cancelled'].includes(String(row.status)) ? null : { status: 'cancelled', cancel_reason: values.reason ?? '' })
    } else if (actionKey === 'batch-online') {
      patchMany(row => row.status === 'online' ? null : { status: 'online' })
    } else if (actionKey === 'batch-offline') {
      patchMany(row => row.status === 'offline' ? null : { status: 'offline', offline_reason: values.reason ?? '' })
    } else if (actionKey === 'batch-review') {
      patchMany(row => row.review_status === 'pending' ? null : { review_status: 'pending' })
    } else if (actionKey === 'batch-approve') {
      patchMany(row => ['approved', 'pending', 'rejected'].includes(String(row.review_status)) ? { review_status: 'approved' } : null)
    } else if (actionKey === 'batch-reject') {
      patchMany(row => row.review_status === 'pending' ? { review_status: 'rejected', reject_reason: values.reason ?? '' } : null)
    } else if (actionKey === 'batch-assign') {
      const owner = this.table('care_team').find(item => item.id === values.owner_id)
      const name = owner?.name ?? values.assignee_name
      if (!name) throw new ApiError('422', '请选择处理人', { owner_id: '请选择处理人' })
      patchMany(row => ({ owner_name: name, assignee_name: name, status: 'processing' }))
    } else if (actionKey === 'batch-remind') {
      patchMany(row => ({
        next_remind_at: nowIso(),
        last_reminded_at: nowIso(),
        remind_count: Number(row.remind_count ?? 0) + 1,
      }))
    } else if (actionKey === 'batch-sync') {
      patchMany(row => ({ last_sync_at: nowIso(), last_report_at: nowIso() }))
    } else if (actionKey === 'batch-generate') {
      patchMany(row => ({ status: 'generating' }))
      const reports = this.table('reports')
      for (const row of rows) {
        reports.unshift({
          id: this.nextId('reports'),
          report_no: businessNo('BG'),
          user_id: row.user_id ?? row.id,
          user_name: row.user_name ?? row.name,
          family_name: row.family_name ?? '',
          period_start: formatDate(new Date(Date.now() - 7 * 86_400_000)),
          period_end: formatDate(new Date()),
          report_type: values.report_type ?? '周报',
          template_name: values.template_name ?? '家庭健康周报',
          status: 'completed',
          generated_at: nowIso(),
          sent_at: '',
          failure_reason: '',
          asset_name: '',
          created_at: nowIso(),
          updated_at: nowIso(),
          row_version: 1,
        })
      }
    } else {
      // Task-backed batch operations still produce a visible task center entry.
      patchMany(() => ({ last_batch_at: nowIso() }))
    }
    this.recordAudit(actor, page.title, '批量操作', `${rows.length} 条记录`, failed.length ? '失败' : '成功', null, null, `${actionKey}：成功 ${succeeded} 条，失败 ${failed.length} 条`)
    return { ok: failed.length === 0, message: failed.length ? `完成 ${succeeded} 条，${failed.length} 条未完成` : `已完成 ${succeeded} 条记录`, failed, succeeded }
  }

  createTask(taskType: string, title: string, total = 1, actor = '陈知远'): TaskRecord {
    const task: TaskRecord = {
      id: this.nextId('tasks'),
      taskNo: businessNo('RW'),
      taskType,
      title,
      status: '进行中',
      progress: 8,
      total,
      success: 0,
      failed: 0,
      message: '任务已进入内存队列，可离开页面稍后查看',
      createdAt: nowIso(),
      retryable: true,
    }
    this.table('async_tasks').unshift(task as unknown as AdminRecord)
    this.recordAudit(actor, '任务中心', '创建任务', title, '成功', null, task as unknown as AdminRecord, `${taskType}任务已创建`)
    return { ...task }
  }

  advanceTasks(): void {
    for (const task of this.table('async_tasks')) {
      const status = String(task.status)
      if (['success', 'partial_success', 'failed', 'cancelled', 'partial'].includes(status)) continue
      const progress = Math.min(100, Number(task.progress ?? 0) + 17)
      task.progress = progress
      task.success = Math.floor((Number(task.total ?? 1) * progress) / 100)
      if (progress >= 100) {
        task.status = 'success'
        task.message = '任务已完成，可在任务中心下载结果'
        task.finished_at = nowIso()
      } else {
        task.status = 'running'
        task.message = '正在处理，可离开页面稍后查看'
      }
    }
  }

  retryTask(id: string): TaskRecord {
    const task = this.find('async_tasks', id)
    if (!task) throw new ApiError('404', '任务不存在')
    task.status = 'running'
    task.progress = 10
    task.failed = 0
    task.message = '已重新进入队列，正在重试失败项'
    task.finished_at = ''
    this.recordAudit('陈知远', '任务中心', '重试任务', String(task.title), '成功', null, task, '重试失败任务')
    return task as unknown as TaskRecord
  }

  cancelTask(id: string): TaskRecord {
    const task = this.find('async_tasks', id)
    if (!task) throw new ApiError('404', '任务不存在')
    if (TERMINAL_TASK.includes(String(task.status))) throw new ApiError('409', '已完成的任务不能取消')
    task.status = 'cancelled'
    task.message = '任务已取消'
    task.finished_at = nowIso()
    return task as unknown as TaskRecord
  }

  // ---------- master data ----------
  masterRows(config: MasterConfig): AdminRecord[] {
    return this.table(config.module).map(row => ({ ...row }))
  }

  masterUsage(config: MasterConfig, row: AdminRecord): number {
    let count = 0
    for (const reference of config.references) {
      for (const target of this.table(reference.module)) {
        const value = target[reference.field]
        if (reference.mode === 'array' && Array.isArray(value) && value.includes(row[config.nameKey])) count += 1
        if (reference.mode === 'scalar' && value === row[config.nameKey]) count += 1
      }
    }
    return count
  }

  masterCreate(config: MasterConfig, input: Record<string, unknown>, actor: string): AdminRecord {
    const values = toPlain(input)
    const name = String(values[config.nameKey] ?? '').trim()
    if (!name) throw new ApiError('422', `请填写${config.title}名称`, { [config.nameKey]: '必填' })
    if (this.table(config.module).some(row => row[config.nameKey] === name)) {
      throw new ApiError('422', `${config.title}已存在`, { [config.nameKey]: '名称重复' })
    }
    const record: AdminRecord = {
      id: this.nextId(config.module),
      ...values,
      status: values.status ?? 'active',
      sort: values.sort ?? this.table(config.module).length + 1,
      usage_count: 0,
      created_at: nowIso(),
      updated_at: nowIso(),
      row_version: 1,
    }
    this.table(config.module).unshift(record)
    this.syncDerived()
    this.recordAudit(actor, config.title, '新增', name, '成功', null, record, `新增${config.title}`)
    return { ...record }
  }

  masterUpdate(config: MasterConfig, id: string, input: Record<string, unknown>, actor: string): AdminRecord {
    const rows = this.table(config.module)
    const index = rows.findIndex(row => String(row.id) === id)
    if (index < 0) throw new ApiError('404', '目标主数据不存在')
    const before: AdminRecord = { ...(rows[index] as AdminRecord) }
    const oldName = String(before[config.nameKey])
    const values = toPlain(input)
    const next: AdminRecord = { ...before, ...values, updated_at: nowIso(), row_version: Number(before.row_version ?? 1) + 1 }
    rows[index] = next
    const newName = String(next[config.nameKey])
    if (newName !== oldName) {
      for (const reference of config.references) {
        for (const target of this.table(reference.module)) {
          const value = target[reference.field]
          if (reference.mode === 'scalar' && value === oldName) target[reference.field] = newName
          if (reference.mode === 'array' && Array.isArray(value)) target[reference.field] = value.map(item => item === oldName ? newName : item)
        }
      }
    }
    this.syncDerived()
    this.recordAudit(actor, config.title, '编辑', newName, '成功', before, next, `编辑${config.title}`)
    return { ...next }
  }

  masterMerge(config: MasterConfig, sourceId: string, targetName: string, actor: string): ActionOutcome {
    const rows = this.table(config.module)
    const source = rows.find(row => String(row.id) === sourceId)
    if (!source) throw new ApiError('404', '源主数据不存在')
    if (source[config.nameKey] === targetName) throw new ApiError('422', '目标和源不能相同')
    let moved = 0
    for (const reference of config.references) {
      for (const target of this.table(reference.module)) {
        const value = target[reference.field]
        if (reference.mode === 'scalar' && value === source[config.nameKey]) {
          target[reference.field] = targetName
          moved += 1
        }
        if (reference.mode === 'array' && Array.isArray(value) && value.includes(source[config.nameKey])) {
          target[reference.field] = value.map(item => item === source[config.nameKey] ? targetName : item)
          moved += 1
        }
      }
    }
    source.status = 'disabled'
    source.merged_into = targetName
    source.updated_at = nowIso()
    this.syncDerived()
    this.recordAudit(actor, config.title, '合并', `${source[config.nameKey]} → ${targetName}`, '成功', null, source, `迁移 ${moved} 处引用`)
    return { message: `已将 ${moved} 处引用迁移到${targetName}，源数据已停用`, row: { ...source } }
  }

  masterRemove(config: MasterConfig, id: string, actor: string): ActionResult {
    const rows = this.table(config.module)
    const index = rows.findIndex(row => String(row.id) === id)
    if (index < 0) throw new ApiError('404', '目标主数据不存在')
    const row = rows[index]!
    const usage = this.masterUsage(config, row)
    if (usage > 0) this.throwConflict('masters', row, config.module, [{ module: config.module, label: config.usageLabel, count: usage }])
    rows.splice(index, 1)
    this.recordAudit(actor, config.title, '删除', String(row[config.nameKey]), '成功', row, null, `删除${config.title}`)
    return { ok: true, message: '已删除主数据', succeeded: 1 }
  }

  migrateReferences(module: 'links' | 'assets', sourceId: string, targetName: string, actor: string): ActionOutcome {
    if (module === 'links') {
      const source = this.find('links', sourceId)
      if (!source) throw new ApiError('404', '链接不存在')
      if (source.name === targetName) throw new ApiError('422', '目标链接不能与源链接相同')
      let moved = 0
      for (const moduleName of ['templates', 'tips', 'faq']) {
        for (const row of this.table(moduleName)) {
          if (row.link_name === source.name) {
            row.link_name = targetName
            moved += 1
          }
        }
      }
      this.syncDerived()
      this.recordAudit(actor, '链接管理', '迁移引用', `${source.name} → ${targetName}`, '成功', null, source, `迁移 ${moved} 处引用`)
      return { message: `已将 ${moved} 处引用迁移至${targetName}，现在可以停用原链接`, row: { ...source } }
    }
    const source = this.find('assets', sourceId)
    if (!source) throw new ApiError('404', '素材不存在')
    let moved = 0
    for (const [moduleName, fields] of Object.entries({
      drugs: ['image_name'],
      tips: ['cover_name'],
      membership_plans: ['cover_name'],
      reports: ['asset_name'],
    })) {
      for (const row of this.table(moduleName)) {
        for (const field of fields) {
          if (row[field] === source.name) {
            row[field] = targetName
            moved += 1
          }
        }
      }
    }
    this.syncDerived()
    this.recordAudit(actor, '附件库', '替换引用', `${source.name} → ${targetName}`, '成功', null, source, `迁移 ${moved} 处引用`)
    return { message: `已将 ${moved} 处引用替换为${targetName}，现在可以归档原素材`, row: { ...source } }
  }

  // ---------- notifications / tasks ----------
  notices(): AdminNotice[] {
    return this.table('admin_notices')
      .map(row => ({
        id: String(row.id),
        type: row.type as AdminNotice['type'],
        title: String(row.title),
        body: String(row.body),
        createdAt: String(row.created_at),
        read: Boolean(row.read),
        linkRoute: String(row.route),
      }))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
  }

  markNoticeRead(id: string): void {
    const notice = this.find('admin_notices', id)
    if (notice) notice.read = true
  }

  markAllNoticesRead(): void {
    for (const notice of this.table('admin_notices')) notice.read = true
  }

  // ---------- platform settings / roles ----------
  updateSettings(input: Partial<PlatformSettings>, actor: string): PlatformSettings {
    const next = { ...this.settings, ...toPlain(input), updatedAt: nowIso(), updatedBy: actor }
    if (Number(next.passwordMinLength) < 8) throw new ApiError('422', '密码最小长度不能小于8位', { passwordMinLength: '不能小于8位' })
    if (Number(next.alertSlaMinutes) <= 0) throw new ApiError('422', '预警 SLA 必须大于0分钟', { alertSlaMinutes: '必须大于0' })
    if (next.maintenanceEnabled && !String(next.maintenanceMessage).trim()) {
      throw new ApiError('422', '启用维护模式时必须填写维护说明', { maintenanceMessage: '必填' })
    }
    const before = { ...this.settings }
    this.settings = next
    this.recordAudit(actor, '系统设置', '保存', '系统参数', '成功', before as unknown as AdminRecord, next as unknown as AdminRecord, '保存平台信息、安全策略、通知默认值、SLA 与维护模式')
    return { ...this.settings }
  }

  updateRolePermissions(
    roleId: string,
    permissions: string[],
    dataScope: string,
    actor: string,
  ): AdminRecord {
    const role = this.find('roles', roleId)
    if (!role) throw new ApiError('404', '角色不存在')
    if (role.is_builtin && role.code === 'super_admin') {
      const required = ['roles.manage', 'admins.manage', 'settings.manage', 'audit.view', 'users.view']
      const missing = required.filter(permission => !permissions.includes(permission))
      if (missing.length) {
        throw new ApiError('409', `内置超级管理员角色不可移除关键权限：${missing.join('、')}`)
      }
      if (dataScope !== 'all') throw new ApiError('409', '内置超级管理员角色必须保持全部平台数据范围')
    }
    const before = { ...role }
    role.permission_codes = unique(permissions)
    role.permission_count = role.permission_codes.length
    role.data_scope = dataScope
    role.data_scope_label = this.dataScopeLabel(dataScope)
    role.updated_at = nowIso()
    role.row_version = Number(role.row_version ?? 1) + 1
    this.recordAudit(actor, '角色权限', '编辑权限', String(role.name), '成功', before, role, `更新为 ${permissions.length} 项权限`)
    return { ...role }
  }

  dataScopeLabel(code: string): string {
    const map: Record<string, string> = {
      all: '全部平台数据',
      business: '全部业务数据',
      assigned: '分配家庭数据',
      catalog: '药品主数据',
      review: '审核数据',
      support: '客服队列',
      device_region: '所辖区域设备',
      aggregate: '聚合分析',
      audit: '审计与运维',
      region: '所辖区域数据',
      content: '内容与素材',
    }
    return map[code] ?? code
  }

  syncPermissionCatalog(actor: string): { permissions: number; modules: number } {
    const count = PERMISSION_GROUPS.reduce((total, group) => total + group.permissions.length, 0)
    this.recordAudit(actor, '角色权限', '同步权限目录', '权限目录', '成功', null, null, `同步 ${count} 项权限，覆盖 ${PERMISSION_GROUPS.length} 个模块`)
    return { permissions: count, modules: PERMISSION_GROUPS.length }
  }

  /**
   * Current permission matrix for a role. The roles table is the editable
   * source of truth in prototype mode; the frozen seed matrix is only a
   * fallback for role codes that have not been materialised yet.
   */
  permissionsForRole(code: string): string[] {
    const role = this.table('roles').find(row => String(row.code) === String(code))
    if (role && role.status === 'disabled') return []
    if (Array.isArray(role?.permission_codes)) return (role.permission_codes as unknown[]).map(String)
    return ROLE_PERMISSIONS[code as keyof typeof ROLE_PERMISSIONS] ?? []
  }

  // ---------- options ----------
  options(source: string): SelectOption[] {
    const active = (module: string) => this.table(module).filter(row => row.status === 'active' || row.status === undefined)
    switch (source) {
      case 'regions':
        return active('regions').map(row => ({ value: String(row.id), label: `${row.name} · ${row.level_label}` }))
      case 'users':
        return this.table('users').filter(row => row.status !== 'cancelled').slice(0, 200).map(row => ({ value: String(row.id), label: `${row.name} · ${String(row.phone).replace(/^(\d{3})\d{4}/, '$1****')}` }))
      case 'families':
        return this.table('families').filter(row => row.status === 'active').map(row => ({ value: String(row.id), label: `${row.name} · ${row.family_no}` }))
      case 'drugs':
        return this.table('drugs').map(row => ({ value: String(row.id), label: `${row.generic_name} · ${row.spec}` }))
      case 'approved_drugs':
        return this.table('drugs').filter(row => row.review_status === 'approved').map(row => ({ value: String(row.id), label: `${row.generic_name} · ${row.spec}` }))
      case 'care_team':
        return this.table('care_team').filter(row => row.status === 'active').map(row => ({ value: String(row.id), label: `${row.name} · ${row.specialty}` }))
      case 'links':
        return active('links').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'roles':
        return active('roles').map(row => ({ value: String(row.code), label: String(row.name) }))
      case 'user_tags':
        return active('health_tags').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'assets':
        return active('assets').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'asset_folders':
        return ASSET_FOLDERS.map(value => ({ value, label: value }))
      case 'link_categories':
        return active('link_categories').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'benefits':
        return active('benefits').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'templates':
        return active('report_templates').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'membership_plans':
        return this.table('membership_plans').filter(row => row.status === 'online').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'admins':
        return this.table('admins').filter(row => row.status === 'active').map(row => ({ value: String(row.id), label: `${row.name} · ${row.account}` }))
      case 'permissions':
        return PERMISSION_GROUPS.flatMap(group => group.permissions.map(permission => ({ value: permission, label: `${group.label} · ${permission}` })))
      case 'notification_templates':
        return active('templates').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'report_templates':
        return active('report_templates').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'faq_categories':
        return active('faq_categories').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'drug_categories':
        return active('drug_categories').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'device_models':
        return active('device_models').map(row => ({ value: String(row.name), label: String(row.name) }))
      case 'devices':
        return this.table('devices')
          .filter(row => !row.user_id || row.status === 'unbound')
          .map(row => ({ value: String(row.id), label: `${row.device_no} · ${row.model_name ?? '设备'}` }))
      default:
        return []
    }
  }

  // ---------- dashboard ----------
  dashboardStats(start: string, end: string, scopeRegionId?: string): DashboardStats {
    const scoped = (row: AdminRecord): boolean => !scopeRegionId || !row.region_id || row.region_id === scopeRegionId
    const users = this.table('users').filter(scoped)
    const families = this.table('families').filter(scoped)
    const alerts = this.table('alerts').filter(scoped)
    const devices = this.table('devices').filter(scoped)
    const records = this.table('medication_records')
    const orders = this.table('orders')
    const startDay = dateKey(start)
    const endDay = dateKey(end)
    const inRange = (value: unknown): boolean => {
      const day = dateKey(value)
      return day >= startDay && day <= endDay
    }
    const alertRange = alerts.filter(row => inRange(row.triggered_at))
    const recordRange = records.filter(row => inRange(row.scheduled_at))
    const userRange = users.filter(row => inRange(row.registered_at))
    const orderRange = orders.filter(row => inRange(row.created_at))
    const paid = orderRange.filter(row => row.status === 'paid' || row.status === 'refund_pending' || row.status === 'refunded')
    const taken = recordRange.filter(row => row.status === 'taken').length
    const adherence = recordRange.length ? Math.round((taken / recordRange.length) * 1000) / 10 : 0
    const online = devices.filter(row => row.status === 'online').length
    const trendDays = this.daysBetween(startDay, endDay)
    const isoDay = (value: unknown): string => dateKey(value).replace(/\//g, '-')
    const trend = trendDays.map(day => ({
      day,
      users: users.filter(row => isoDay(row.last_active_at) === day).length,
      records: records.filter(row => isoDay(row.scheduled_at) === day).length,
      alerts: alerts.filter(row => isoDay(row.triggered_at) === day).length,
      orders: orders.filter(row => isoDay(row.created_at) === day).length,
    }))
    const risk = ['低风险', '中风险', '高风险'].map((label, index) => ({
      label,
      value: families.filter(row => row.risk_level === label).length,
      tone: index === 2 ? 'danger' : index === 1 ? 'warning' : 'success',
    }))
    const channels = unique(orders.map(row => String(row.channel))).map(label => ({
      label,
      value: orders.filter(row => row.channel === label).length,
    }))
    const regions = this.table('regions').filter(row => row.level === 'district').map(row => ({
      label: String(row.name),
      users: users.filter(user => user.region_id === row.id).length,
      families: families.filter(family => family.region_id === row.id).length,
      alerts: alerts.filter(alert => alert.family_id && families.some(family => family.id === alert.family_id && family.region_id === row.id)).length,
    })).filter(row => row.users || row.families).sort((left, right) => right.users - left.users).slice(0, 8)
    const pendingAlerts = alerts.filter(row => row.status === 'pending').length
    const pendingDrugs = this.table('drugs').filter(row => row.review_status === 'pending').length
    const urgentTickets = this.table('tickets').filter(row => row.priority === '紧急' && row.status !== 'closed').length
    const failedTasks = this.table('async_tasks').filter(row => row.status === 'failed').length
    const todos = [
      { id: 'todo-alerts', title: `${pendingAlerts} 条健康预警待处理`, meta: '按 SLA 优先处理高风险预警', route: '#/health/alerts?status=pending', tone: 'danger' },
      { id: 'todo-drugs', title: `${pendingDrugs} 个药品等待审核`, meta: '核对批准文号与说明书后上架', route: '#/drugs/review?review_status=pending', tone: 'warning' },
      { id: 'todo-tickets', title: `${urgentTickets} 个紧急工单待首响`, meta: '客服 SLA 计时中', route: '#/support/tickets?priority=紧急', tone: 'warning' },
      { id: 'todo-tasks', title: `${failedTasks} 个异步任务失败`, meta: '可重试失败项并下载结果', route: '#/settings/operations', tone: 'info' },
    ].filter(item => !item.title.startsWith('0 '))
    const recent = this.table('audit_logs').slice(0, 8).map(row => ({ ...row }))
    return {
      usersTotal: users.length,
      activeUsers: users.filter(row => row.status === 'active').length,
      families: families.length,
      adherence,
      pendingAlerts,
      highAlerts: alerts.filter(row => row.severity === '高').length,
      onlineRate: devices.length ? Math.round((online / devices.length) * 1000) / 10 : 0,
      deviceTotal: devices.length,
      revenueFen: paid.reduce((sum, row) => sum + Number(row.amount_fen ?? 0), 0),
      paidOrders: paid.length,
      pendingDrugs,
      urgentTickets,
      failedTasks,
      trend,
      risk,
      channels,
      regions,
      todos,
      recent,
    }
  }

  private daysBetween(start: string, end: string): string[] {
    const iso = (value: string): string => value.replace(/\//g, '-')
    const startDate = new Date(`${iso(start)}T00:00:00+08:00`)
    const endDate = new Date(`${iso(end)}T00:00:00+08:00`)
    const days: string[] = []
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return days
    let cursor = startDate.getTime()
    let guard = 0
    while (cursor <= endDate.getTime() && guard < 120) {
      const local = new Date(cursor + 8 * 3_600_000)
      days.push(`${local.getUTCFullYear()}-${String(local.getUTCMonth() + 1).padStart(2, '0')}-${String(local.getUTCDate()).padStart(2, '0')}`)
      cursor += 86_400_000
      guard += 1
    }
    return days
  }

  search(term: string): SearchResultGroup[] {
    const value = term.trim().toLowerCase()
    if (!value) return []
    const pick = (module: string, titleKey: string, subKeys: string[], route: (row: AdminRecord) => string, label: string, limit = 5): SearchResultGroup => {
      const items = this.table(module).filter(row => {
        const haystack = [row[titleKey], ...subKeys.map(key => row[key])].map(item => String(item ?? '')).join(' ').toLowerCase()
        return haystack.includes(value)
      }).slice(0, limit).map(row => ({
        id: String(row.id),
        title: String(row[titleKey] ?? '—'),
        sub: subKeys.map(key => row[key]).filter(Boolean).slice(0, 2).join(' · '),
        route: route(row),
        group: label,
      }))
      return { label, items }
    }
    return [
      pick('users', 'name', ['user_no', 'phone', 'family_name'], row => `#/users?keyword=${encodeURIComponent(String(row.name))}`, '用户'),
      pick('families', 'name', ['family_no', 'owner_name'], row => `#/families?keyword=${encodeURIComponent(String(row.name))}`, '家庭'),
      pick('drugs', 'generic_name', ['trade_name', 'approval_no'], row => `#/drugs?keyword=${encodeURIComponent(String(row.generic_name))}`, '药品'),
      pick('orders', 'order_no', ['user_name', 'plan_name'], row => `#/membership/orders?keyword=${encodeURIComponent(String(row.order_no))}`, '会员订单'),
      pick('tickets', 'ticket_no', ['title', 'user_name'], row => `#/support/tickets?keyword=${encodeURIComponent(String(row.ticket_no))}`, '客服工单'),
    ].filter(group => group.items.length)
  }

  // ---------- decoration ----------
  decorationPages(): AdminRecord[] {
    return this.table('decoration_pages').map(row => ({ ...row }))
  }

  decorationConfig(pageKey: string): AdminRecord | undefined {
    const config = this.table('decoration_configs').find(row => row.page_key === pageKey)
    return config ? JSON.parse(JSON.stringify(config)) as AdminRecord : undefined
  }

  saveDecoration(pageKey: string, components: AdminRecord[], status: 'draft' | 'published', actor: string): AdminRecord {
    const config = this.table('decoration_configs').find(row => row.page_key === pageKey)
    if (!config) throw new ApiError('404', '页面装修配置不存在')
    const before = { ...config }
    config.components = toPlain(components)
    config.status = status
    config.version = Number(config.version ?? 1) + 1
    config.saved_by = actor
    config.saved_at = nowIso()
    config.updated_at = nowIso()
    config.row_version = Number(config.row_version ?? 1) + 1
    this.recordAudit(actor, '装修中心', status === 'published' ? '保存并同步' : '保存草稿', String(config.page_title), '成功', before, config, `保存 ${components.length} 个组件`)
    return JSON.parse(JSON.stringify(config)) as AdminRecord
  }

  // ---------- assets ----------
  addAsset(input: { name: string; folder: string; type: string; sizeKb: number; broken?: boolean }, actor: string): AdminRecord {
    const asset: AdminRecord = {
      id: this.nextId('assets'),
      name: input.name,
      asset_type: input.type,
      mime_type: input.type === '图片' ? 'image/png' : input.type === '文档' ? 'application/pdf' : 'video/mp4',
      size_kb: input.sizeKb,
      width: input.type === '图片' ? 1280 : 0,
      height: input.type === '图片' ? 720 : 0,
      folder: input.folder,
      references: '刚刚上传，尚未被引用',
      usage_count: 0,
      status: 'active',
      cover_color: ['#d9f5ea', '#e3f0fe', '#fdeef3', '#fff4df', '#f1eefe'][Math.floor(Math.random() * 5)],
      preview_error: Boolean(input.broken),
      uploaded_at: nowIso(),
      created_at: nowIso(),
      updated_at: nowIso(),
      row_version: 1,
    }
    this.table('assets').unshift(asset)
    this.recordAudit(actor, '附件库', '上传', input.name, '成功', null, asset, '上传素材到内存附件库')
    return { ...asset }
  }

  // ---------- auth ----------
  findAdminByAccount(account: string): AdminRecord | undefined {
    return this.table('admins').find(row => row.account === account)
  }
}

export const adapter = new PrototypeAdapter()

/** Used by browser and unit regression tests to prove proxies are not cloned directly. */
export function plainQuery(query: FilterState): ListQuery {
  return {
    page: 1,
    pageSize: 20,
    keyword: query.keyword,
    status: query.status,
    filters: toPlain(query.filters),
    dateStart: query.dateStart,
    dateEnd: query.dateEnd,
  }
}

export function summaryFor(page: PageDefinition, records: AdminRecord[]): Record<string, number> {
  return summarize(records, page)
}
